import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  Building2,
  Loader2,
  Mail,
  Plus,
  Shield,
  Trash2,
  User,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import { type ApiUser, type UserProfile, profileTypeColors, roleLabels } from '../model';

interface Affiliate {
  id: string;
  name: string;
}

interface BulkResults {
  results: Array<{ email: string; status: 'created' | 'skipped'; reason?: string }>;
  summary: { total: number; created: number; skipped: number };
}

export function BulkUploadDialog({
  showBulkModal,
  setShowBulkModal,
  bulkRole,
  setBulkRole,
  affiliates,
  bulkAffiliateId,
  setBulkAffiliateId,
  bulkEmails,
  setBulkEmails,
  bulkResults,
  handleBulkUpload,
  isBulkUploading,
}: {
  showBulkModal: boolean;
  setShowBulkModal: (value: boolean) => void;
  bulkRole: string;
  setBulkRole: (value: string) => void;
  affiliates: Affiliate[];
  bulkAffiliateId: string;
  setBulkAffiliateId: (value: string) => void;
  bulkEmails: string;
  setBulkEmails: (value: string) => void;
  bulkResults: BulkResults | null;
  handleBulkUpload: () => Promise<void>;
  isBulkUploading: boolean;
}) {
  return (
    <>
      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-4">
          <Card className="w-full max-w-lg mx-4 animate-fade-in max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Bulk Upload Users
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowBulkModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Add multiple users at once. No invites will be sent — you can send invites
                separately.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Role for all users</Label>
                <Select
                  options={[
                    { value: 'hq_admin', label: 'HQ Admin' },
                    { value: 'agent', label: 'Agent' },
                    { value: 'affiliate_owner', label: 'Affiliate Owner' },
                    { value: 'affiliate_coach', label: 'Affiliate Coach' },
                    { value: 'member', label: 'Member' },
                    { value: 'individual', label: 'Individual' },
                  ]}
                  value={bulkRole}
                  onChange={(e) => {
                    setBulkRole(e.target.value);
                    setBulkAffiliateId('');
                  }}
                />
              </div>
              {['member', 'affiliate_coach'].includes(bulkRole) && affiliates.length > 0 && (
                <div className="space-y-2">
                  <Label>Assign to Gym {bulkRole === 'affiliate_coach' ? '*' : '(optional)'}</Label>
                  <Select
                    options={[
                      { value: '', label: 'Select a gym...' },
                      ...affiliates.map((a) => ({ value: a.id, label: a.name })),
                    ]}
                    value={bulkAffiliateId}
                    onChange={(e) => setBulkAffiliateId(e.target.value)}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Email addresses (one per line)</Label>
                <textarea
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  placeholder={'john@example.com\njane@example.com\nmike@example.com'}
                  value={bulkEmails}
                  onChange={(e) => setBulkEmails(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {bulkEmails.split('\n').filter((l) => l.trim() && l.includes('@')).length} valid
                  emails detected
                </p>
              </div>
              {bulkResults && (
                <div className="rounded-lg border p-3 space-y-2">
                  <p className="text-sm font-medium">
                    Results: {bulkResults.summary.created} created, {bulkResults.summary.skipped}{' '}
                    skipped
                  </p>
                  {bulkResults.results
                    .filter((r) => r.status === 'skipped')
                    .map((r) => (
                      <p key={r.email} className="text-xs text-amber-500">
                        {r.email}: {r.reason}
                      </p>
                    ))}
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowBulkModal(false)}
                >
                  {bulkResults ? 'Close' : 'Cancel'}
                </Button>
                {!bulkResults && (
                  <Button className="flex-1" onClick={handleBulkUpload} disabled={isBulkUploading}>
                    {isBulkUploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Users className="mr-2 h-4 w-4" />
                        Upload Users
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

export function InviteUserDialog({
  showAddModal,
  setShowAddModal,
  inviteEmail,
  setInviteEmail,
  inviteName,
  setInviteName,
  inviteRole,
  setInviteRole,
  inviteAffiliateId,
  setInviteAffiliateId,
  inviteAffiliateName,
  setInviteAffiliateName,
  affiliates,
  handleInviteUser,
  isInviting,
}: {
  showAddModal: boolean;
  setShowAddModal: (value: boolean) => void;
  inviteEmail: string;
  setInviteEmail: (value: string) => void;
  inviteName: string;
  setInviteName: (value: string) => void;
  inviteRole: string;
  setInviteRole: (value: string) => void;
  inviteAffiliateId: string;
  setInviteAffiliateId: (value: string) => void;
  inviteAffiliateName: string;
  setInviteAffiliateName: (value: string) => void;
  affiliates: Affiliate[];
  handleInviteUser: () => Promise<void>;
  isInviting: boolean;
}) {
  return (
    <>
      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          {/* max-h + overflow so the "Create new gym" branch can't push the
              Cancel/Send-Invite buttons off-screen (matches Bulk Upload + View modals). */}
          <Card className="w-full max-w-md mx-4 animate-fade-in max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-primary" />
                  Invite User
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>Send an invitation to a new user</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email Address *</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="user@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-name">Name (optional)</Label>
                <Input
                  id="invite-name"
                  placeholder="John Doe"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select
                  options={[
                    { value: 'hq_admin', label: 'HQ Admin' },
                    { value: 'agent', label: 'Agent' },
                    { value: 'affiliate_owner', label: 'Affiliate Owner' },
                    { value: 'affiliate_coach', label: 'Affiliate Coach' },
                    { value: 'member', label: 'Member' },
                    { value: 'individual', label: 'Individual' },
                  ]}
                  value={inviteRole}
                  onChange={(e) => {
                    setInviteRole(e.target.value);
                    setInviteAffiliateId('');
                    setInviteAffiliateName('');
                  }}
                />
              </div>
              {/* Gym assignment for affiliate owners */}
              {inviteRole === 'affiliate_owner' && (
                <div className="space-y-3">
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                    <p className="text-sm text-amber-500 font-medium">
                      Has the gym been created yet?
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Every owner needs a gym. You can assign an existing one or create a new one.
                    </p>
                  </div>
                  {affiliates.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="invite-existing-gym">Assign to Existing Gym</Label>
                      <Select
                        options={[
                          { value: '', label: 'Create a new gym instead...' },
                          ...affiliates.map((a) => ({ value: a.id, label: a.name })),
                        ]}
                        value={inviteAffiliateId}
                        onChange={(e) => {
                          setInviteAffiliateId(e.target.value);
                          if (e.target.value) setInviteAffiliateName('');
                        }}
                      />
                    </div>
                  )}
                  {!inviteAffiliateId && (
                    <div className="space-y-2">
                      <Label htmlFor="invite-gym-name">New Gym Name *</Label>
                      <Input
                        id="invite-gym-name"
                        placeholder="e.g. CrossFit Hackney"
                        value={inviteAffiliateName}
                        onChange={(e) => setInviteAffiliateName(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        A new gym will be created and this user will be set as the owner.
                      </p>
                    </div>
                  )}
                </div>
              )}
              {/* Gym Assignment - for coaches and members */}
              {['member', 'affiliate_coach'].includes(inviteRole) && (
                <div className="space-y-2">
                  <Label htmlFor="invite-gym">
                    Assign to Gym {inviteRole === 'affiliate_coach' ? '*' : '(optional)'}
                  </Label>
                  <Select
                    options={[
                      { value: '', label: 'Select a gym...' },
                      ...affiliates.map((a) => ({ value: a.id, label: a.name })),
                    ]}
                    value={inviteAffiliateId}
                    onChange={(e) => setInviteAffiliateId(e.target.value)}
                  />
                  {affiliates.length === 0 && (
                    <p className="text-xs text-amber-500">
                      No gyms in the system yet. Invite an Affiliate Owner first to create one.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {inviteRole === 'affiliate_coach'
                      ? 'Coaches must be assigned to a gym.'
                      : 'Optionally assign this member to a gym.'}
                  </p>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button className="flex-1" onClick={handleInviteUser} disabled={isInviting}>
                  {isInviting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Send Invite
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

export function AddProfileDialog({
  showAddProfileModal,
  selectedUser,
  setShowAddProfileModal,
  addProfileType,
  setAddProfileType,
  hqAdminConfirmText,
  setHqAdminConfirmText,
  userProfiles,
  addProfileTerritoryName,
  setAddProfileTerritoryName,
  affiliates,
  addProfileAffiliateId,
  setAddProfileAffiliateId,
  addProfileMemberRole,
  setAddProfileMemberRole,
  handleAddProfile,
  isAddingProfile,
}: {
  showAddProfileModal: boolean;
  selectedUser: ApiUser | null;
  setShowAddProfileModal: (value: boolean) => void;
  addProfileType: string;
  setAddProfileType: (value: string) => void;
  hqAdminConfirmText: string;
  setHqAdminConfirmText: (value: string) => void;
  userProfiles: UserProfile[];
  addProfileTerritoryName: string;
  setAddProfileTerritoryName: (value: string) => void;
  affiliates: Affiliate[];
  addProfileAffiliateId: string;
  setAddProfileAffiliateId: (value: string) => void;
  addProfileMemberRole: 'owner' | 'coach' | 'member';
  setAddProfileMemberRole: (value: 'owner' | 'coach' | 'member') => void;
  handleAddProfile: () => Promise<void>;
  isAddingProfile: boolean;
}) {
  return (
    <>
      {/* Add Profile Modal (HQ-012) */}
      {showAddProfileModal && selectedUser && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4 animate-fade-in">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-primary" />
                  Add Role
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddProfileModal(false);
                    setAddProfileType('');
                    setHqAdminConfirmText('');
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>Select a role to add for {selectedUser.name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Step 1: Role Selection Cards */}
              {!addProfileType && (
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      { type: 'hq_admin', label: 'HQ Admin', icon: Shield, subtitle: undefined },
                      { type: 'agent', label: 'Agent', icon: UserCheck, subtitle: undefined },
                      {
                        type: 'affiliate_member',
                        label: 'Affiliate',
                        icon: Building2,
                        subtitle: 'Owner / Coach / Member',
                      },
                      { type: 'individual', label: 'Individual', icon: User, subtitle: undefined },
                    ] as const
                  ).map(({ type, label, icon: RoleIcon, subtitle }) => {
                    // affiliate_member allows multiple (different gyms), others are one-per-user
                    // Only check active profiles — suspended ones can be reactivated from the list
                    const hasRole =
                      type === 'affiliate_member'
                        ? false
                        : userProfiles.some((p) => p.profile_type === type && p.is_active);
                    const gymCount =
                      type === 'affiliate_member'
                        ? userProfiles.filter(
                            (p) => p.profile_type === 'affiliate_member' && p.is_active
                          ).length
                        : 0;
                    const colorClass =
                      profileTypeColors[type] || 'bg-gray-500/10 text-gray-400 border-gray-500/20';
                    return (
                      <button
                        key={type}
                        type="button"
                        disabled={hasRole}
                        onClick={() => setAddProfileType(type)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-colors ${
                          hasRole
                            ? 'opacity-40 cursor-not-allowed border-border'
                            : 'hover:border-primary/50 cursor-pointer border-border hover:bg-accent/50'
                        }`}
                      >
                        <div className={`rounded-full p-2 ${colorClass}`}>
                          <RoleIcon className="h-5 w-5" />
                        </div>
                        <span className="text-sm font-medium">{label}</span>
                        {subtitle && (
                          <span className="text-xs text-muted-foreground">{subtitle}</span>
                        )}
                        {hasRole ? (
                          <span className="text-xs text-muted-foreground">Assigned</span>
                        ) : gymCount > 0 ? (
                          <span className="text-xs text-muted-foreground">
                            {gymCount} gym{gymCount > 1 ? 's' : ''} — add more
                          </span>
                        ) : (
                          <Plus className="h-4 w-4 text-primary" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Step 2: Role-specific fields */}
              {addProfileType === 'hq_admin' && (
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                    <p className="text-sm font-medium text-destructive mb-2">
                      You are about to grant HQ Admin access to {selectedUser.name}.
                    </p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Type <strong>HQ ADMIN</strong> to confirm:
                    </p>
                    <Input
                      placeholder="Type HQ ADMIN"
                      value={hqAdminConfirmText}
                      onChange={(e) => setHqAdminConfirmText(e.target.value)}
                      className="font-mono"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setAddProfileType('');
                        setHqAdminConfirmText('');
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={handleAddProfile}
                      disabled={isAddingProfile || hqAdminConfirmText !== 'HQ ADMIN'}
                    >
                      {isAddingProfile ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Confirm
                    </Button>
                  </div>
                </div>
              )}

              {addProfileType === 'agent' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Territory Name *</Label>
                    <Input
                      placeholder="e.g. UK South"
                      value={addProfileTerritoryName}
                      onChange={(e) => setAddProfileTerritoryName(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setAddProfileType('')}
                    >
                      Back
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={handleAddProfile}
                      disabled={isAddingProfile || !addProfileTerritoryName.trim()}
                    >
                      {isAddingProfile ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Add Agent Role
                    </Button>
                  </div>
                </div>
              )}

              {addProfileType === 'affiliate_member' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Select Gym *</Label>
                    <Select
                      options={[
                        { value: '', label: 'Choose a gym...' },
                        ...affiliates.map((a) => ({ value: a.id, label: a.name })),
                      ]}
                      value={addProfileAffiliateId}
                      onChange={(e) => setAddProfileAffiliateId(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Role at Gym *</Label>
                    <Select
                      options={[
                        { value: 'member', label: 'Member' },
                        { value: 'coach', label: 'Coach' },
                        { value: 'owner', label: 'Owner' },
                      ]}
                      value={addProfileMemberRole}
                      onChange={(e) =>
                        setAddProfileMemberRole(e.target.value as 'owner' | 'coach' | 'member')
                      }
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setAddProfileType('')}
                    >
                      Back
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={handleAddProfile}
                      disabled={isAddingProfile || !addProfileAffiliateId}
                    >
                      {isAddingProfile ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Add Affiliate Role
                    </Button>
                  </div>
                </div>
              )}

              {addProfileType === 'individual' && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    This will add an Individual subscription profile to {selectedUser.name}.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setAddProfileType('')}
                    >
                      Back
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={handleAddProfile}
                      disabled={isAddingProfile}
                    >
                      {isAddingProfile ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Add Individual Role
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

export function DeleteUserDialog({
  showDeleteModal,
  selectedUser,
  setShowDeleteModal,
  deleteConfirmText,
  setDeleteConfirmText,
  confirmDeleteUser,
  isDeleting,
}: {
  showDeleteModal: boolean;
  selectedUser: ApiUser | null;
  setShowDeleteModal: (value: boolean) => void;
  deleteConfirmText: string;
  setDeleteConfirmText: (value: string) => void;
  confirmDeleteUser: () => Promise<void>;
  isDeleting: boolean;
}) {
  return (
    <>
      {/* Delete User Confirmation Modal (GDPR) */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4 animate-fade-in">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-red-500">
                  <Trash2 className="h-5 w-5" />
                  Delete User
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowDeleteModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                This action is irreversible and will permanently delete the user&apos;s data in
                compliance with GDPR.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-4">
                <p className="text-sm font-medium text-red-500">You are about to delete:</p>
                <div className="mt-2 space-y-1">
                  <p className="text-sm">
                    <span className="text-muted-foreground">Name:</span> {selectedUser.name}
                  </p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Email:</span> {selectedUser.email}
                  </p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Role:</span>{' '}
                    {roleLabels[selectedUser.role] || selectedUser.role}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delete-confirm">
                  Type <span className="font-mono font-bold text-red-500">DELETE</span> to confirm
                </Label>
                <Input
                  id="delete-confirm"
                  placeholder="Type DELETE"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                  className="font-mono"
                />
              </div>

              <div className="text-xs text-muted-foreground">
                <p>This will:</p>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li>Remove the user from our system</li>
                  <li>Delete all personal data</li>
                  <li>Remove from Clerk authentication</li>
                  <li>Send a GDPR confirmation email</li>
                </ul>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={confirmDeleteUser}
                  disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete User
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
